import { meetingRowAction, MEETING_STATE_BADGE, type UpcomingMeetingState } from './meeting-row-state';

/**
 * What one upcoming-meeting row draws: its action, its badge, the "Notes will be taken" chip and the line under it.
 *
 * The server's `meetingState` is the one field to render from (VMR #35330, design: the phase 1 close-out page). A
 * row without it comes from a site the TypeScript harness still serves, and keeps the old `state` behaviour, so
 * both shapes go through this one function and no call site branches on which server answered.
 *
 * Import-free for the reason `meeting-row-state.ts` gives. Web only for now: iOS and Android read `state`.
 */

export const MEETING_STATES = ['not_added', 'will_join', 'in_meeting', 'ended_waiting_for_notes', 'notes_ready', 'no_notes'] as const;
export type MeetingState = (typeof MEETING_STATES)[number];

export const MEETING_OUTCOME_REASONS = [
  'not_admitted',
  'nobody_joined',
  'nothing_heard',
  'removed_by_host',
  'join_failed',
  'zoom_no_permission',
  'meet_host_settings',
] as const;
export type MeetingOutcomeReason = (typeof MEETING_OUTCOME_REASONS)[number];

export interface MeetingRowFields {
  state: UpcomingMeetingState;
  meetingState?: MeetingState;
  /** Only with `no_notes`. */
  outcomeReason?: MeetingOutcomeReason;
  /** Remove before the meeting. False once she is sent, and in the last minute before the start. */
  removable?: boolean;
  /** The owner declined it in their calendar. */
  declined?: boolean;
  /** A removal's leave failed: she may still be in the call. */
  removeFailed?: boolean;
}

/** `add` / `remove` are the phase 1 verbs; `join` / `skip` / `undo` are a row without `meetingState`. */
export type MeetingAction = 'add' | 'remove' | 'join' | 'skip' | 'undo' | 'none';

type MeetingBadgeVariant = (typeof MEETING_STATE_BADGE)[UpcomingMeetingState] | 'done';

export interface MeetingView {
  action: MeetingAction;
  /** `label` is an i18n key. `connectors.meetings.state.scheduled` reads "{agent} will join" where the name is known. */
  badge: { variant: MeetingBadgeVariant; label: string } | null;
  notesChip: boolean;
  /** An i18n key for the line under the row, or null. */
  hint: string | null;
  removeFailed: boolean;
}

const STATE = 'connectors.meetings.state.';
const HINT = 'connectors.meetings.hint.';

export function phase1State(meeting: Pick<MeetingRowFields, 'meetingState'>): MeetingState | null {
  const value = meeting.meetingState;
  return value && (MEETING_STATES as readonly string[]).includes(value) ? value : null;
}

export function meetingView(meeting: MeetingRowFields): MeetingView {
  const state = phase1State(meeting);
  if (!state) {
    return {
      action: meetingRowAction(meeting.state),
      badge: { variant: MEETING_STATE_BADGE[meeting.state], label: `${STATE}${meeting.state}` },
      notesChip: false,
      hint: null,
      removeFailed: false,
    };
  }
  const removeFailed = meeting.removeFailed === true;
  switch (state) {
    case 'not_added':
      return meeting.declined === true
        ? { action: 'none', badge: { variant: 'waiting-on-a-reply', label: `${STATE}not_scheduled` }, notesChip: false, hint: `${HINT}declined`, removeFailed }
        : { action: 'add', badge: { variant: 'waiting-on-a-reply', label: `${STATE}not_scheduled` }, notesChip: false, hint: null, removeFailed };
    case 'will_join': {
      const removable = meeting.removable === true;
      return {
        action: removable ? 'remove' : 'none',
        badge: { variant: 'connected', label: `${STATE}scheduled` },
        notesChip: true,
        hint: removable ? null : `${HINT}onTheWay`,
        removeFailed,
      };
    }
    case 'in_meeting':
      // `removable` is false once she is sent, so Remove in the meeting reads the state (the spec, P0-3).
      return { action: 'remove', badge: { variant: 'in-progress', label: `${STATE}in_meeting` }, notesChip: true, hint: null, removeFailed };
    case 'ended_waiting_for_notes':
      return { action: 'none', badge: null, notesChip: false, hint: `${HINT}notesSoon`, removeFailed };
    case 'notes_ready':
      return { action: 'none', badge: { variant: 'done', label: `${STATE}notes_ready` }, notesChip: false, hint: null, removeFailed };
    case 'no_notes': {
      const reason = meeting.outcomeReason;
      const known = reason !== undefined && (MEETING_OUTCOME_REASONS as readonly string[]).includes(reason);
      return {
        action: 'none',
        badge: { variant: 'failed', label: `${STATE}no_notes` },
        notesChip: false,
        hint: known ? `connectors.meetings.noNotes.${reason}` : null,
        removeFailed,
      };
    }
  }
}

/** The `decision` a row's action sends. `null` clears the row back to what the connection says. */
export function meetingActionDecision(action: Exclude<MeetingAction, 'none'>): 'join' | 'skip' | 'remove' | null {
  if (action === 'add' || action === 'join') return 'join';
  if (action === 'skip') return 'skip';
  if (action === 'remove') return 'remove';
  return null;
}

const AFTER: readonly MeetingState[] = ['ended_waiting_for_notes', 'notes_ready', 'no_notes'];

/** A meeting that is behind her: never the Next up card. */
export function meetingIsOver(meeting: Pick<MeetingRowFields, 'state' | 'meetingState'>): boolean {
  const state = phase1State(meeting);
  return state ? AFTER.includes(state) : meeting.state === 'failed';
}

/** The meeting's end has passed by `now`. An unknown end never passes. */
export function meetingHasEnded(meeting: { endsAt?: string }, now: number): boolean {
  const endsAt = meeting.endsAt ? Date.parse(meeting.endsAt) : NaN;
  return Number.isFinite(endsAt) && endsAt <= now;
}

/**
 * The rows the list shows. An ended meeting stays only once the server has moved it to an after-meeting state (the
 * server keeps today's ended meetings so the outcome shows). Any other ended row leaves: the list is read once, so a
 * `will_join` row past its end is stale, not a meeting still ahead.
 */
export function meetingsShown<T extends { endsAt?: string; state: UpcomingMeetingState; meetingState?: MeetingState }>(
  meetings: readonly T[],
  now: number,
): T[] {
  return meetings.filter((meeting) => !meetingHasEnded(meeting, now) || (phase1State(meeting) !== null && meetingIsOver(meeting)));
}

export interface MeetingStatus {
  badge: { variant: MeetingBadgeVariant; label: string } | null;
  /** The line under it: a removal that failed (an alert, since she may still be recording), else the row's hint. */
  note: { text: string; alert: boolean } | null;
}

type Translate = (key: string, params?: Record<string, unknown>) => string;

/** The badge and the line every surface draws for a row, in words. One function, so no surface drops a field. */
export function meetingStatus(meeting: MeetingRowFields, t: Translate, agent: string | undefined): MeetingStatus {
  const view = meetingView(meeting);
  const badge = view.badge && {
    variant: view.badge.variant,
    label:
      view.badge.label === `${STATE}scheduled` && agent ? t(`${STATE}scheduledBy`, { agent }) : t(view.badge.label),
  };
  const note = view.removeFailed
    ? { text: t('connectors.meetings.removeFailed', { agent }), alert: true }
    : view.hint
    ? { text: t(view.hint, { agent }), alert: false }
    : null;
  return { badge, note };
}
